import type { Namespace } from '@govard/i18n-core';

import { shellRu } from '../ru/shell.js';

const generated = Object.fromEntries(
  Object.keys(shellRu).map((key) => [key, key.split('.').at(-1)?.replaceAll('_', ' ') ?? key]),
);

export const shellEn: Namespace = {
  ...generated,
  'language.label': 'Language',
  'language.ru': 'RU',
  'language.en': 'EN',
  'sidebar.group_sales': 'Sales',
  'sidebar.group_tasks': 'Tasks',
  'sidebar.group_content': 'Content',
  'sidebar.group_analytics': 'Analytics',
  'sidebar.group_automation': 'Automation',
  'sidebar.group_telegram': 'Telegram',
  'sidebar.group_admin': 'Admin',
  'sidebar.collapse': 'Collapse sidebar',
  'sidebar.expand': 'Expand sidebar',
  'sidebar.admin_profile': 'Admin profile',
  'sidebar.admin_name': 'Admin',
  'sidebar.system_owner': 'System owner',
  'topbar.search_placeholder': 'Search GovardOS...',
  'topbar.notifications': 'Notifications',
  'topbar.settings': 'Settings',
  'operator.restored': 'The operator module route is available in the CRM shell.',
  'operator.parity_title': 'Route skeleton',
  'operator.parity_description':
    'The route is available in the app shell for ERP2 parity. Module implementation is planned for [PHASE-2].',
  'operator.skeleton_only': 'Only the route skeleton is currently available without runtime logic.',
  'operator.module.prompt_compiler': 'Prompt Compiler',
  'operator.module.knowledge': 'Knowledge Base',
  'operator.module.files': 'Files',
  'operator.module.agent_monitor': 'Agent Monitor',
  'operator.module.opencode': 'OpenCode',
  'system_agent.group_none': 'System',
  'system_agent.group_workspace': 'Workspace',
  'system_agent.group_deal': 'Deals',
  'system_agent.group_contact': 'Contacts',
  'system_agent.session_fallback': 'Session {id}',
  'system_agent.global_context': 'global context',
  'auth.login.session_error': 'Could not save the session. Try again.',
  'auth.login.invalid_credentials': 'Incorrect email or password.',
  'auth.login.email_taken': 'A user with this email already exists.',
  'auth.login.rate_limited': 'Too many attempts. Try again later.',
  'auth.login.registration_unavailable': 'Registration is unavailable in demo mode.',
  'auth.login.status_error': 'Authentication error ({status}).',
  'auth.login.connection_error': 'Could not authenticate. Check your connection.',
  'auth.guard.unavailable_title': 'Could not verify your session',
  'auth.guard.unavailable_description':
    'The service is temporarily unavailable. Check your connection and try again.',
  'auth.login.subtitle': 'Sign in to your workspace.',
  'auth.login.register_subtitle': 'Create your personal workspace.',
  'auth.login.tab_login': 'Sign in',
  'auth.login.tab_register': 'Register',
  'auth.login.name': 'Name',
  'auth.login.name_required': 'Enter your name',
  'auth.login.name_too_long': 'Name is too long',
  'auth.login.email': 'Email',
  'auth.login.email_required': 'Enter your email',
  'auth.login.email_invalid': 'Enter a valid email',
  'auth.login.password': 'Password',
  'auth.login.password_required': 'Enter your password',
  'auth.login.password_min': 'Password must be at least 8 characters',
  'auth.login.submit': 'Sign in',
  'auth.login.create_account': 'Create account',
  'auth.login.forgot_password': 'Forgot password?',
  'auth.email_required': 'Enter an email address',
  'auth.email_invalid': 'Enter a valid email address',
  'auth.forgot.rate_limited': 'Too many requests. Try again later.',
  'auth.forgot.send_error': 'Could not send the email. Try again later.',
  'auth.forgot.title': 'Reset password',
  'auth.forgot.sent_title': 'Email sent',
  'auth.forgot.sent_description':
    'If this address is registered, it will receive a password reset link valid for one hour.',
  'auth.forgot.back_to_login': 'Back to sign in',
  'auth.forgot.description':
    'Enter the email address registered to your account. We will send a password reset link.',
  'auth.forgot.submit': 'Send link',
  'auth.forgot.remembered': 'Remembered your password?',
  'auth.verify.title': 'Verify email',
  'auth.verify.success_title': 'Email verified',
  'auth.verify.success_description': '{email} is now verified.',
  'auth.verify.open_app': 'Open app',
  'auth.verify.failed_title': 'Link did not work',
  'auth.verify.failed_description':
    'It may have expired or already been used. Request a new email in settings.',
  'auth.verify.settings': 'Open settings',
  'auth.reset.expired': 'This link has expired. Request a new one.',
  'auth.reset.error': 'Could not change the password. Try again later.',
  'auth.reset.title': 'New password',
  'auth.reset.success_title': 'Password changed',
  'auth.reset.success_description':
    'All previous sessions were closed. Sign in again with your new password.',
  'auth.reset.to_login': 'Go to sign in',
  'auth.reset.description':
    'Create a new password of at least 8 characters. All sessions on other devices will be closed.',
  'auth.reset.new_link': 'Request a new link',
  'auth.reset.password': 'New password',
  'auth.reset.password_required': 'Enter a password',
  'auth.reset.password_min': 'At least 8 characters',
  'auth.reset.repeat': 'Repeat password',
  'auth.reset.repeat_required': 'Repeat the password',
  'auth.reset.mismatch': 'Passwords do not match',
  'auth.reset.submit': 'Change password',
  'shared.columns': 'Columns',
  'shared.reset_view': 'Reset view',
  'shared.configure_columns': 'Configure columns',
  'shared.retry': 'Retry',
  'shared.retry_fallback': 'Try the request again.',
  'shared.total_count': 'Total: {count}',
  'shared.delete': 'Delete',
  'shared.delete_error': 'Could not delete{detail}',
  'shared.overdue': 'Overdue',
  'shared.entity_type.deal': 'Deal',
  'shared.entity_type.contact': 'Contact',
  'shared.entity_type.company': 'Company',
  'shared.entity_type.task': 'Task',
  'shared.entity_type.project': 'Project',
  'shared.entity_type.document': 'Document',
  'shared.entity_type.meeting': 'Meeting',
  'shared.entity_type.template': 'Template',
  'shared.calendar.date': 'Date',
  'shared.calendar.time': 'Time',
  'shared.calendar.event': 'Event',
  'shared.calendar.all_day': 'All day',
  'shared.calendar.week': 'Week',
  'shared.calendar.work_week': 'Work week',
  'shared.calendar.day': 'Day',
  'shared.calendar.month': 'Month',
  'shared.calendar.previous': 'Back',
  'shared.calendar.next': 'Next',
  'shared.calendar.yesterday': 'Yesterday',
  'shared.calendar.tomorrow': 'Tomorrow',
  'shared.calendar.today': 'Today',
  'shared.calendar.agenda': 'Agenda',
  'shared.calendar.empty': 'No events in this range',
  'shared.calendar.show_more': '+{count} more',
  'invite.not_found': 'Invitation not found. The link may be incomplete.',
  'invite.load_error': 'Could not open the invitation.',
  'invite.wrong_email':
    'This invitation was sent to another address. Sign in with the address that received it.',
  'invite.accept_error': 'Could not accept the invitation.',
  'invite.unavailable': 'Invitation unavailable',
  'invite.invalid': 'Invalid invitation',
  'invite.reason_revoked': 'This invitation was revoked.',
  'invite.reason_accepted': 'This invitation has already been accepted.',
  'invite.reason_expired': 'This invitation has expired. Ask for a new one.',
  'invite.reason_unknown': 'This link no longer works.',
  'invite.prefix': 'You are invited to workspace',
  'invite.as_role': 'as {role}',
  'invite.for_email': 'The invitation was sent to',
  'invite.role_admin': 'administrator',
  'invite.role_member': 'member',
  'invite.role_viewer': 'viewer',
  'invite.sign_in_description':
    'To accept, sign in with this address. If you do not have an account, register with the same address and return to this link.',
  'invite.sign_in': 'Sign in or register',
  'invite.signed_in_as': 'Signed in as {email}.',
  'invite.accept': 'Accept invitation',
  'invite.title': 'Govard CRM invitation',
  'not_found.title': 'Page not found',
  'not_found.description': 'Check the address or return to the home page.',
  'not_found.home': 'Home',
  'not_found.login': 'Sign in',
  'team.role.owner': 'Owner',
  'team.role.admin': 'Administrator',
  'team.role.member': 'Member',
  'team.role.viewer': 'Viewer',
  'team.member_status.active': 'Active',
  'team.member_status.suspended': 'Suspended',
  'team.member_status.removed': 'Removed',
  'team.invitation_status.pending': 'Pending',
  'team.invitation_status.accepted': 'Accepted',
  'team.invitation_status.revoked': 'Revoked',
  'team.invited_by': 'invited by {name}',
  'shared.inherited_link_tooltip': '{via}: this record has no direct link',
  'shared.inherited_via.contact': 'Via contact',
  'shared.inherited_via.deal': 'Via deal',
  'shared.inherited_via.document': 'Via document',
  'shared.inherited_via.company': 'Via company',
  'shared.inherited_via.task': 'Via task',
  'shared.inherited_via.meeting': 'Via meeting',
  'shared.inherited_via.template': 'Via template',
  'topbar.theme': 'Theme',
  'topbar.theme_light': 'Light',
  'topbar.theme_dark': 'Dark',
  'topbar.theme_system': 'System',
  'topbar.open_menu': 'Open menu',
  'sidebar.close': 'Close',
  'common.reload': 'Reload',
};
